/**
 * 역할·entityType·subdomain read caps 기반 메뉴 가시성 훅
 *
 * PRODUCTIZATION_PROGRESS.md §3.1: 역할별 메뉴/화면 분리
 * - external: Entity > 외부 조직만 노출
 * - internal delegated JWT: subdomain read 권한 없는 도메인 비노출 (TP-G26 · D23)
 * - export/system delegated: export:read|system:read → export 메뉴, system:read|export:read → system 메뉴 (owner는 전체)
 * - user / null(미인증): fail-closed 기본값 — global-search·workspace만 노출 (N1 RBAC 수정,
 *   과거 "전체 메뉴"는 entityType!=='internal' 이 fullCaps를 반환하던 business-menu-rbac 버그였음)
 */

import { useMemo } from 'react';
import {
  canAccessBusinessPath,
  deriveBusinessMenuAccessCaps,
  filterBusinessMenuKeys,
  filterEntitySubPathsByCaps,
} from '@shared/site/business-menu-rbac';
import { getCurrentTokenPayload } from '../../utils/jwtUtils';

export type EntitySubPath = '/entity/internal' | '/entity/external' | '/entity/user';

export interface MenuVisibility {
  /** 현재 사용자 entityType (토큰 없으면 null — fail-closed 제한 처리, internal과 동일하지 않음) */
  entityType: 'internal' | 'external' | 'user' | null;
  /** 사이드바/헤더에 노출할 도메인 키 (entity, item, finance, ...) */
  visibleDomainKeys: string[];
  /** Entity 하위에 노출할 경로 (내부 포지션, 외부 조직, 사용자) */
  entitySubPaths: EntitySubPath[];
  /** Internal 사용자 여부 (external/user만 제한 시 사용) */
  isInternal: boolean;
  /** owner | system:admin — adminOnly 메뉴 노출 */
  hasFullMenuAccess: boolean;
  /** 해당 경로 접근 가능 여부 */
  canAccessPath: (path: string) => boolean;
}

const ALL_DOMAIN_KEYS = [
  'global-search',
  'workspace',
  'entity',
  'item',
  'finance',
  'finance-advanced',
  'space',
  'knowledge',
  'appeal',
  'calendar',
  'automation',
  'manufacturing',
  'log',
  'export',
  'conscious',
  'flow',
  'system',
];

const ALL_ENTITY_SUB_PATHS: EntitySubPath[] = [
  '/entity/internal',
  '/entity/external',
  '/entity/user',
];

/**
 * 권한·entityType에 따라 노출할 메뉴를 반환한다.
 * 토큰이 없거나 payload.entityType이 'internal'이 아니면 fail-closed 제한 처리
 * (N1 RBAC 수정 — 과거 전체 노출은 보안 버그였음).
 */
export function useMenuVisibility(): MenuVisibility {
  return useMemo(() => {
    const payload = getCurrentTokenPayload();
    const entityType = (payload?.entityType ?? null) as MenuVisibility['entityType'];
    const permissions = payload?.permissions ?? [];
    const role = payload?.role;

    if (entityType === 'external') {
      const visibleDomainKeys = ['global-search', 'entity'];
      const entitySubPaths: EntitySubPath[] = ['/entity/external'];
      return {
        entityType: 'external',
        visibleDomainKeys,
        entitySubPaths,
        isInternal: false,
        hasFullMenuAccess: false,
        canAccessPath: (path: string) => {
          if (path === '/entity' || path.startsWith('/entity/')) {
            return path === '/entity/external' || path.startsWith('/entity/external/');
          }
          return false;
        },
      };
    }

    const accessSource = {
      entityType,
      permissions,
      ...(role !== undefined ? { role } : {}),
    };
    const caps = deriveBusinessMenuAccessCaps(accessSource);
    const visibleDomainKeys = filterBusinessMenuKeys(ALL_DOMAIN_KEYS, accessSource);
    const entitySubPaths = filterEntitySubPathsByCaps(
      ALL_ENTITY_SUB_PATHS,
      caps
    ) as EntitySubPath[];

    return {
      entityType,
      visibleDomainKeys,
      entitySubPaths,
      isInternal: entityType === 'internal',
      hasFullMenuAccess: caps.hasFullMenuAccess,
      canAccessPath: (path: string) =>
        canAccessBusinessPath(path, visibleDomainKeys, caps, entityType, role),
    };
  }, []);
}
